import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { categories } from '../mock';
import { productsAPI } from '../services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import './Catalog.css';

// Composant carrousel d'images
const ImageCarousel = ({ images, image, name, onImageClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Support des anciens produits avec image unique
  const imageList = images && images.length > 0 ? images : (image ? [image] : []);

  if (imageList.length === 0) {
    return <div className="carousel-placeholder">Aucune image</div>;
  }

  const goToPrevious = (e) => {
    e.stopPropagation();
    setCurrentIndex(prev => prev === 0 ? imageList.length - 1 : prev - 1);
  };

  const goToNext = (e) => {
    e.stopPropagation();
    setCurrentIndex(prev => prev === imageList.length - 1 ? 0 : prev + 1);
  };

  return (
    <div className="image-carousel" onClick={onImageClick}>
      <img
        src={imageList[currentIndex]}
        alt={`${name} - ${currentIndex + 1}`}
        className="carousel-image"
      />
      {imageList.length > 1 && (
        <>
          <button className="carousel-btn carousel-btn-left" onClick={goToPrevious}>
            <ChevronLeft size={20} />
          </button>
          <button className="carousel-btn carousel-btn-right" onClick={goToNext}>
            <ChevronRight size={20} />
          </button>
          <div className="carousel-dots">
            {imageList.map((_, idx) => (
              <span
                key={idx}
                className={`carousel-dot ${idx === currentIndex ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentIndex(idx);
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const Catalog = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [dialogImageIndex, setDialogImageIndex] = useState(0);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await productsAPI.getAll();
      setProducts(data);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort products - featured first
  const filteredProducts = (selectedCategory === 'all'
    ? products
    : products.filter(p => p.category === selectedCategory)
  ).sort((a, b) => {
    // Featured products first
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    return 0;
  });

  // Helper pour obtenir la liste d'images
  const getImageList = (product) => {
    if (product.images && product.images.length > 0) return product.images;
    if (product.image) return [product.image];
    return [];
  };

  return (
    <div className="catalog">
      {/* Partners Section */}
      <div className="partners-section">
        <div className="container">
          <img 
            src="https://customer-assets.emergentagent.com/job_phoenix-store-1/artifacts/k1zjg6u6_Ils%20nous%20font%20confiance.png" 
            alt="Ils nous font confiance - Nos partenaires"
            className="partners-image"
          />
        </div>
      </div>

      <div className="catalog-content section-padding">
        <div className="container">
          {/* Category Filter */}
          <div className="filter-section">
            <div className="filter-header">
              <Filter size={20} />
              <span>Catégories</span>
            </div>
            <div className="category-filters">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  className={`category-btn ${selectedCategory === cat.id ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(cat.id)}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          {/* Products Grid */}
          <div className="catalog-grid">
            {loading ? (
              <div className="loading-text">Chargement des produits...</div>
            ) : filteredProducts.length === 0 ? (
              <div className="no-products">
                <p>Aucun produit trouvé dans cette catégorie.</p>
              </div>
            ) : (
              filteredProducts.map((product) => (
                <div key={product.id} className="product-card premium-card fade-in-up">
                  <div className="product-image-wrapper">
                    <ImageCarousel 
                      images={product.images}
                      image={product.image}
                      name={product.name}
                      onImageClick={() => {
                        setSelectedProduct(product);
                        setDialogImageIndex(0);
                      }}
                    />
                    {product.featured && (
                      <div className="product-badge">Tendance</div>
                    )}
                  </div>
                  <div className="product-info">
                    <span className="product-category">{product.category}</span>
                    <h3 className="product-name">{product.name}</h3>
                    {product.quantity_available > 0 && (
                      <p className="product-stock">✅ {product.quantity_available} en stock</p>
                    )}
                    {product.dimensions && (
                      <p className="product-dimensions">📐 {product.dimensions}</p>
                    )}
                    {product.min_quantity && product.min_quantity > 1 && (
                      <p className="product-min-qty">📦 Minimum: {product.min_quantity} unités</p>
                    )}
                    <div className="product-footer">
                      <span className="product-price">{product.price.toFixed(2)} € HT</span>
                      <button 
                        onClick={() => {
                          setSelectedProduct(product);
                          setDialogImageIndex(0);
                        }}
                        className="product-link-button"
                      >
                        Voir détails
                        <ArrowRight size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Product Details Dialog */}
      <Dialog open={selectedProduct !== null} onOpenChange={() => setSelectedProduct(null)}>
        <DialogContent className="product-dialog">
          {selectedProduct && (
            <div className="dialog-scroll-container">
              <DialogHeader>
                <DialogTitle>{selectedProduct.name}</DialogTitle>
              </DialogHeader>
              <div className="dialog-product-content">
                {/* Image gallery with navigation */}
                <div className="dialog-images">
                  {getImageList(selectedProduct).length > 0 ? (
                    <>
                      <div className="dialog-main-image-container">
                        <img
                          src={getImageList(selectedProduct)[dialogImageIndex]}
                          alt={selectedProduct.name}
                          className="dialog-product-image"
                        />
                        {getImageList(selectedProduct).length > 1 && (
                          <>
                            <button 
                              className="dialog-nav-btn dialog-nav-left"
                              onClick={() => setDialogImageIndex(prev => prev === 0 ? getImageList(selectedProduct).length - 1 : prev - 1)}
                            >
                              <ChevronLeft size={24} />
                            </button>
                            <button 
                              className="dialog-nav-btn dialog-nav-right"
                              onClick={() => setDialogImageIndex(prev => prev === getImageList(selectedProduct).length - 1 ? 0 : prev + 1)}
                            >
                              <ChevronRight size={24} />
                            </button>
                          </>
                        )}
                      </div>
                      {getImageList(selectedProduct).length > 1 && (
                        <>
                          <div className="dialog-image-thumbnails">
                            {getImageList(selectedProduct).map((img, idx) => (
                              <img
                                key={idx}
                                src={img}
                                alt={`${selectedProduct.name} ${idx + 1}`}
                                className={`dialog-thumbnail ${idx === dialogImageIndex ? 'active' : ''}`}
                                onClick={() => setDialogImageIndex(idx)}
                              />
                            ))}
                          </div>
                          <div className="dialog-image-counter">
                            {dialogImageIndex + 1} / {getImageList(selectedProduct).length}
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <div className="dialog-no-image">Aucune image disponible</div>
                  )}
                </div>
                <div className="dialog-product-info">
                  <div className="dialog-category-badge">{selectedProduct.category}</div>
                  <p className="dialog-description">{selectedProduct.description}</p>
                  {selectedProduct.quantity_available > 0 && (
                    <p className="dialog-details dialog-stock"><strong>✅ En stock:</strong> {selectedProduct.quantity_available} unités disponibles</p>
                  )}
                  {selectedProduct.dimensions && (
                    <p className="dialog-details"><strong>📐 Dimensions:</strong> {selectedProduct.dimensions}</p>
                  )}
                  {selectedProduct.min_quantity && selectedProduct.min_quantity > 1 && (
                    <p className="dialog-details"><strong>📦 Quantité minimum:</strong> {selectedProduct.min_quantity} unités</p>
                  )}
                  <div className="dialog-price">{selectedProduct.price.toFixed(2)} € HT</div>
                  <Link to="/contact" className="btn-primary" onClick={() => setSelectedProduct(null)}>
                    Demander un devis
                    <ArrowRight size={20} />
                  </Link>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Catalog;
